
import EazyHeading from '@/components/EazyHeading.vue';
import assessmentsService from '@/logic/services/assessmentsService';
import store from '@/store/index';
import router from '@/router/index';
import requestAPI from '@/logic/requestBeApi';
import { helpToApplyTakenSlots } from '@/logic/helpers/bookingSlots/helpToApplyTakenSlots';
import exitModal from '@/logic/exitModal';
import getMeetingDateAndTime from '@/logic/helpers/dates/getMeetingDateAndTime';

export default {
  name: 'AssessorConfirmAssessment',

  components: {
    EazyHeading,
  },

  props: {
    inModal: Boolean,
    assessmentIdFromModal: {
      type: String,
      default: '',
    },
    candidateFromModal: {
      type: String,
      default: '',
    },
    candidateIdFromModal: {
      type: String,
      default: '',
    },
    profilePicFromModal: {
      type: String,
      default: '',
    },
  },

  data(): Record<string, unknown> {
    return {
      router,
      selectedDate: '',
      confirmingAssessment: false,
      bookedDates: [],
      assessorCancellingAssessment: false,
      getMeetingDateAndTime,
      unavailableDates: 0,
      allDatesAreAlreadyBooked: false,
    };
  },

  computed: {
    assessmentId(): string {
      return this.$route.query.assessmentID || this.assessmentIdFromModal;
    },

    meetingDetails(): unknown {
      return store.state.meetingDetails;
    },

    meetingDetailsPending(): boolean {
      return store.state.meetingDetailsPending;
    },

    datesSelectedByCandidate(): string[] | [] {
      if (!this.meetingDetails) return [];

      return this.meetingDetails.dates
        ?.filter((date: { status: string }) => date.status === 'selected_by_candidate')
        ?.sort(
          (a: { date_start: string | number | Date }, b: { date_start: string | number | Date }) =>
            new Date(a.date_start).getTime() - new Date(b.date_start).getTime(),
        );
    },

    assessmentLength(): number {
      const firstDateObj = this.meetingDetails.dates[0];

      return (
        new Date(firstDateObj.date_end).getHours() - new Date(firstDateObj.date_start).getHours()
      );
    },

    candidate(): { fullName: string; id: string } {
      let candFromQuery;

      if (!this.inModal && this.$route.query.cand.includes('_')) {
        candFromQuery = {
          fullName: this.$route.query.cand.replace('_', ' '),
          id: this.$route.query.candID,
        };
      }

      return candFromQuery || { fullName: this.candidateFromModal, id: this.candidateIdFromModal };
    },

    profilePic(): string {
      return this.$route.query.pic || this.profilePicFromModal;
    },

    apartmentNumber(): string {
      return this.meetingDetails.location.address.apartment_number;
    },

    physicalPlace(): boolean {
      return this.meetingDetails.meeting_type === 'a physical place';
    },

    fetchingAssessmentDates(): boolean {
      return store.state.assessmentDatesPending;
    },

    assessmentDates(): unknown {
      return store.state.assessmentDates;
    },

    dateFrom: {
      get(): string {
        if (
          this.meetingDetails.status === 'cancelled_by_candidate' ||
          !this.datesSelectedByCandidate
        )
          return '';

        return this.datesSelectedByCandidate[0].date_start.split('T')[0];
      },

      set(newVal: string): string {
        return newVal;
      },
    },

    dateTo: {
      get(): string {
        if (
          this.meetingDetails.status === 'cancelled_by_candidate' ||
          !this.datesSelectedByCandidate
        )
          return '';

        return this.datesSelectedByCandidate[
          this.datesSelectedByCandidate.length - 1
        ].date_start.split('T')[0];
      },

      set(newVal: string): string {
        return newVal;
      },
    },

    bookedDatesOnlyDates(): string[] {
      return this.bookedDates.map((obj: { bookedDate: string }) => obj.bookedDate);
    },

    cancellationType(): string {
      const currentAssessment = JSON.parse(localStorage.getItem('currentAssessment'));
      return currentAssessment ? currentAssessment.cancellation_type : '';
    },

    candidateDecisionHeading(): string {
      const cancelledOrDeclined = this.meetingDetails.date
        ? this.$t('candidateCancelledAssessment')
        : this.$t('candidateDeclinedInvitation');

      return this.cancellationType === 'CANDIDATE_POSTPONED'
        ? this.$t('candidatePostponedAssessment')
        : cancelledOrDeclined;
    },

    videoCallInfo(): string {
      return this.meetingDetails?.comment || this.meetingDetails?.invitation_comment || '';
    },
  },

  watch: {
    assessmentId(): void {
      assessmentsService.getAssessmentMeeting({
        self: this,
        assessmentId: this.assessmentId,
      });
    },

    datesSelectedByCandidate(): void {
      if (this.meetingDetails.status === 'cancelled_by_candidate' || !this.datesSelectedByCandidate)
        return;

      if (this.datesSelectedByCandidate?.length === 1) {
        this.selectedDate = this.datesSelectedByCandidate[0].date_start;
      }

      assessmentsService.getAssessmentDates({
        self: this,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
      });
    },

    assessmentDates(): void {
      if (!this.assessmentDates.length) return;

      this.bookedDates = helpToApplyTakenSlots(this.assessmentDates, 'date');
    },

    bookedDatesOnlyDates(): void {
      this.checkHowManyDatesUnavailable(this.datesSelectedByCandidate);
    },
  },

  created(): void {
    assessmentsService.getAssessmentMeeting({
      self: this,
      assessmentId: this.assessmentId,
    });
  },

  beforeDestroy(): void {
    store.commit('setMeetingDetails', '');
  },

  methods: {
    async confirmAssessment(): Promise<void> {
      this.confirmingAssessment = true;

      const payload = {
        selected_date: this.selectedDate,
      };

      const resp = await requestAPI({
        url: `/api/v1/assessments/${this.assessmentId}/confirm`,
        method: 'PATCH',
        contentType: 'application/merge-patch+json',
        payload,
      });

      if (resp.ok) {
        this.$message.success(this.$t('assessmentSuccessfullyConfirmed'));
        this.inModal ? exitModal('interviews', true) : router.push('/assessor-tasks');
      } else if (resp.status !== 401) this.$message.error(this.$t('someErrorOccurred'));

      this.confirmingAssessment = false;
    },

    beginRescheduling(): void {
      if (this.inModal) this.$emit('rescheduleMeetingDespiteCandSentResponse', true);
      else
        router.push({
          path: '/calendar',
          query: {
            cand: `${this.candidate.fullName.split(' ')[0]}_${this.candidate.fullName
              .split(' ')
              .filter((part: unknown, i: number) => i !== 0)
              .join('_')}`,
            candID: this.candidate.id,
            pic: this.profilePic,
            assessmentID: this.assessmentId,
          },
        });
    },

    askIfUserReallyWantsToCancel(): void {
      this.$prompt(this.$t('areYouSureYouWantToCancelAssessment'), this.$t('cancelAssessment'), {
        confirmButtonText: this.$t('cancelAssessment'),
        confirmButtonClass: 'el-button--danger',
        cancelButtonText: this.$t('goBack'),
        inputPlaceholder: this.$t('cancellationReason'),
        inputPattern: /^[a-zA-Z0-9]+/,
        inputType: 'string',
        inputErrorMessage: this.$t('aboveInputCannotBeEmpty'),
        type: 'warning',
      })
        .then(({ value }: { value: string }) => {
          this.cancelAssessment(value);
        })
        .catch((err: Error) => err);
    },

    async cancelAssessment(cancellationReason: string): Promise<void> {
      this.assessorCancellingAssessment = true;

      const payload = {
        cancellation_reason: cancellationReason,
        cancellation_type: 'ASSESSOR_CANCELLED_PROCESS',
      };

      const resp = await requestAPI({
        url: `/api/v1/assessments/${this.assessmentId}/cancel`,
        method: 'PATCH',
        contentType: 'application/merge-patch+json',
        payload,
      });

      if (resp.ok) {
        this.$message.info(this.$t('assessmentCancelled'));
        this.assessorCancellingAssessment = false;
        store.commit('setMeetingDetails', '');
        this.inModal ? exitModal('interviews', true) : router.push('/assessor-tasks');
      } else if (resp.status !== 401) this.$message.error(this.$t('someErrorOccurred'));

      this.assessorCancellingAssessment = false;
    },

    findBookedCandidate(date: string | number | Date): unknown {
      return this.bookedDates.find(
        (obj: { bookedDate: string }) => obj.bookedDate === new Date(date).toJSON(),
      ).bookedCandidate;
    },

    differentCand(date: unknown): boolean {
      return this.findBookedCandidate(date).id !== this.candidate.id;
    },

    checkHowManyDatesUnavailable(dates: unknown[]): void {
      if (!this.bookedDatesOnlyDates.length) return;

      dates.forEach((date: { date_start: string | number | Date }) => {
        const dateBooked =
          this.bookedDatesOnlyDates.includes(new Date(date.date_start).toJSON()) &&
          this.differentCand(date.date_start);

        if (dateBooked) {
          this.unavailableDates++;

          if (this.unavailableDates === this.datesSelectedByCandidate.length) {
            this.allDatesAreAlreadyBooked = true;
          }
        }
      });
    },
  },
};
